import { IconFromSet } from '@gettactic/components';
import { splitCamelCase } from '@gettactic/helpers/src/strings/splitCamelCase';
import {
  Circle,
  CircleDotDashed,
  Cuboid,
  Eye,
  EyeOff,
  Group as GroupIcon,
  Image,
  Lock,
  Type,
  Unlock
} from 'lucide-react';
import React from 'react';
import { Shape } from '../../../../types';
import { LayerActions } from '../../../LayerActions';
import { useMapCanvas } from '../../contexts/MapCanvasContext';
import { useMapEditor } from '../../contexts/MapEditorContext';

export interface LayerItemProps {
  shape: Shape;
  isSelected: boolean;
  depth?: number;
  onClick: (e: React.MouseEvent) => void;
  isFirst?: boolean;
  isLast?: boolean;
}

export const LayerItem: React.FC<LayerItemProps> = ({
  shape,
  isSelected,
  depth = 0,
  onClick,
  isFirst = false,
  isLast = false
}) => {
  const { updateEditingZoneSize } = useMapCanvas();
  const {
    updateShape: onShapeUpdate,
    deleteShape,
    moveShape,
    locateShape,
    ungroupShapes,
    selectedIds,
    groupSelected
  } = useMapEditor();

  const onMoveUp = () => moveShape(shape.id, 'up');
  const onMoveDown = () => moveShape(shape.id, 'down');
  const onDelete = () => deleteShape(shape.id);
  const onLocate = () => locateShape(shape.id);
  const onUngroup = () => ungroupShapes(shape.id);
  const onGroup = groupSelected;

  // Helper: returns the default name for the shape.
  const getName = () => {
    switch (shape.type) {
      case 'text':
        return shape.text.slice(0, 20) + (shape.text.length > 20 ? '...' : '');
      case 'rectangle':
        return 'Rectangle';
      case 'group':
        return 'Group';
      case 'spot':
        return shape.label;
      case 'svg':
        return 'SVG Shape';
      case 'circle':
        return 'Circle';
      case 'icon':
        if (shape.icon.set === 'mdi') {
          return splitCamelCase(shape.icon.icon.replace(/^mdi/, ''));
        }
        if (shape.icon.set === 'lucide') {
          return splitCamelCase(shape.icon.icon);
        }
        return shape.icon.icon;
      case 'furniture':
        return shape.furniture.name;
      default:
        return 'Shape';
    }
  };

  // Local state for inline editing.
  const [isEditing, setIsEditing] = React.useState(false);
  const initialLabel = shape.layerLabel || getName();
  const [editedName, setEditedName] = React.useState(initialLabel);

  // Update local state when the shape changes (if not currently editing).
  React.useEffect(() => {
    if (!isEditing) {
      setEditedName(shape.layerLabel || getName());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape.layerLabel, shape]);

  // Commits the edit by updating the shape (if changed).
  const commitEdit = () => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== (shape.layerLabel || getName())) {
      onShapeUpdate(shape.id, { layerLabel: trimmedName });
    }
    setIsEditing(false);
  };

  // Cancel the editing and revert to the original label.
  const handleCancel = () => {
    setEditedName(shape.layerLabel || getName());
    setIsEditing(false);
  };

  // Allow commit on Enter and cancel on Escape.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commitEdit();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Start editing on double-click.
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleUpdateEditingArea = () => {
    updateEditingZoneSize(shape.width, shape.height);
  };

  // Returns the icon corresponding to the shape type.
  const getIcon = () => {
    switch (shape.type) {
      case 'text':
        return <Type size={16} />;
      case 'group':
        return <GroupIcon size={16} />;
      case 'svg':
        return <Image size={16} />;
      case 'spot':
        return <CircleDotDashed size={16} />;
      case 'circle':
        return <Circle size={16} />;
      case 'icon':
        return <IconFromSet icon={shape.icon} size={4} />;
      case 'furniture':
        // TODO - Must replace with a furniture preview component
        return (
          <img
            alt={shape.furniture.name}
            src={shape.furniture.url}
            style={{ width: 16, height: 16 }}
          />
        );
      default:
        return <Cuboid size={16} />;
    }
  };

  // Use the provided layerLabel if available or default to getName()
  const layerName = shape.layerLabel || getName();

  return (
    <div>
      <div
        className={`select-none flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${
          isSelected ? 'bg-blue-100' : 'hover:bg-gray-100'
        }`}
        style={{ paddingLeft: `${depth * 16 + 12}px` }}
        onClick={onClick}
      >
        <div className={'w-6'}>{getIcon()}</div>

        {isEditing ? (
          <div className="flex items-center flex-grow">
            <input
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onKeyDown={handleKeyDown}
              className="text-sm flex-grow border border-gray-300 rounded px-1 py-0.5"
            />
          </div>
        ) : (
          <span
            title={layerName}
            className="text-sm flex-grow truncate"
            onDoubleClick={handleDoubleClick}
          >
            {layerName}
          </span>
        )}

        <div
          className="flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="p-1 rounded hover:bg-gray-200 transition-colors text-gray-600"
            onClick={() => onShapeUpdate(shape.id, { hidden: !shape.hidden })}
            title={shape.hidden ? 'Show' : 'Hide'}
          >
            {shape.hidden ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
          <button
            className="p-1 rounded hover:bg-gray-200 transition-colors text-gray-600"
            onClick={() => onShapeUpdate(shape.id, { locked: !shape.locked })}
            title={shape.locked ? 'Unlock' : 'Lock'}
          >
            {shape.locked ? <Lock size={16} /> : <Unlock size={16} />}
          </button>
          <LayerActions
            onDelete={onDelete}
            onLocate={onLocate}
            onMoveUp={!isLast ? onMoveUp : undefined}
            onMoveDown={!isFirst ? onMoveDown : undefined}
            onUngroup={shape.type === 'group' ? onUngroup : undefined}
            onGroup={selectedIds.length > 1 ? onGroup : undefined}
            onUpdateEditingArea={
              shape.layerType === 'background'
                ? handleUpdateEditingArea
                : undefined
            }
          />
        </div>
      </div>
      {shape.type === 'group' && (
        <div className={'ml-6 border-l border-2'}>
          {shape.children.map((child, childIndex) => (
            <LayerItem
              shape={child}
              key={child.id}
              isSelected={false}
              isFirst={childIndex === 0}
              isLast={shape.children.length === childIndex - 1}
              onClick={() => null}
            />
          ))}
        </div>
      )}
    </div>
  );
};
